/**
 * GeneratorPage — Generateur de documents astrologiques
 *
 * Selection d'un client avec theme natal importe,
 * apercu des donnees disponibles, puis generation
 * de documents (Word pour l'instant).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import { generateNatalDocx } from '../services/docxGenerator.js';
import type { Client, ClientsResponse, ClientDetail, ChartSummary, ChartAnalysis } from '../types/clients.js';

// ─── Types locaux ────────────────────────────────────────

interface PlanetRaw {
  nom: string;
  degre: string;
  signe: string;
  maison: number | string;
  retrograde: boolean;
  longitude: number;
}

interface HouseRaw {
  numero: number;
  degre: string;
  signe: string;
  longitude: number;
}

interface DonneesJson {
  planetes: PlanetRaw[];
  maisons: HouseRaw[];
  source?: string;
  client?: {
    prenom: string;
    nom: string;
    dateNaissance: string;
    heureNaissance: string;
    lieuNaissance: string;
  };
}

interface ChartRaw {
  id: number;
  clientId: number;
  type: string;
  donneesJson: DonneesJson | string;
}

/** Parse donneesJson qui peut etre une string JSON ou un objet */
function parseDonnees(raw: DonneesJson | string): DonneesJson {
  if (typeof raw === 'string') return JSON.parse(raw) as DonneesJson;
  return raw;
}

// ─── Constantes ──────────────────────────────────────────

const DOC_TYPES = [
  {
    id: 'data',
    label: 'Fichier DATA',
    description:
      'Export structure des donnees du theme natal pour interpretation IA. Contient les positions planetaires, aspects, configurations et signature energetique.',
    icon: 'bi-file-earmark-code',
    bg: '#f0f9ff',
    fg: '#0369a1',
    format: '.txt',
    ready: false,
  },
  {
    id: 'word',
    label: 'Document Word enrichi',
    description:
      'Document Word complet avec positions, signature energetique, aspects et configurations. Pret a imprimer ou envoyer au client.',
    icon: 'bi-file-earmark-word',
    bg: '#f0fdf4',
    fg: '#16a34a',
    format: '.docx',
    ready: true,
  },
  {
    id: 'markdown',
    label: 'Document Markdown',
    description:
      'Version texte structuree du theme natal. Facilement editable et convertible en PDF. Ideal pour archivage ou retouches manuelles.',
    icon: 'bi-file-earmark-text',
    bg: '#FBF6EC',
    fg: colors.accent,
    format: '.md',
    ready: false,
  },
];

// ─── Composant principal ─────────────────────────────────

export function GeneratorPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  // Donnees du client selectionne
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [selectedChartId, setSelectedChartId] = useState<number | null>(null);
  const [chartRaw, setChartRaw] = useState<ChartRaw | null>(null);
  const [analysisPreview, setAnalysisPreview] = useState<ChartAnalysis | null>(null);

  // Parse donneesJson (peut etre string ou objet)
  const chartDonnees = chartRaw ? parseDonnees(chartRaw.donneesJson) : null;
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Generation
  const [generating, setGenerating] = useState<string | null>(null);

  // Charger la liste des clients
  useEffect(() => {
    apiFetch<ClientsResponse>('/clients?limit=100')
      .then((res) => {
        if (res.success && res.data) {
          setClients(res.data.clients.filter((c) => c.charts.length > 0));
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Charger le detail client quand selectionne
  useEffect(() => {
    if (!selectedClientId) {
      setClientDetail(null);
      setSelectedChartId(null);
      setChartRaw(null);
      setAnalysisPreview(null);
      return;
    }

    setLoadingPreview(true);
    apiFetch<ClientDetail>(`/clients/${selectedClientId}`)
      .then((res) => {
        if (res.success && res.data) {
          setClientDetail(res.data);
          // Selectionner le premier chart natal automatiquement
          const natal = res.data.charts.find((c) => c.type === 'NATAL');
          if (natal) {
            setSelectedChartId(natal.id);
          } else if (res.data.charts.length > 0) {
            setSelectedChartId(res.data.charts[0]!.id);
          }
        }
      })
      .finally(() => setLoadingPreview(false));
  }, [selectedClientId]);

  // Charger l'apercu du chart selectionne
  useEffect(() => {
    if (!selectedClientId || !selectedChartId) {
      setChartRaw(null);
      setAnalysisPreview(null);
      return;
    }

    setLoadingPreview(true);
    Promise.all([
      apiFetch<ChartRaw>(`/clients/${selectedClientId}/charts/${selectedChartId}`),
      apiFetch<ChartAnalysis>(`/clients/${selectedClientId}/charts/${selectedChartId}/analysis`),
    ])
      .then(([chartRes, analysisRes]) => {
        if (chartRes.success && chartRes.data) setChartRaw(chartRes.data);
        if (analysisRes.success && analysisRes.data) setAnalysisPreview(analysisRes.data);
      })
      .finally(() => setLoadingPreview(false));
  }, [selectedClientId, selectedChartId]);

  /** Generer le document Word */
  async function handleGenerateWord() {
    if (!clientDetail || !selectedChartId || !chartDonnees || !analysisPreview) return;

    setGenerating('word');
    try {
      const chart: ChartSummary = clientDetail.charts.find((c) => c.id === selectedChartId)!;
      // Construire le ChartData attendu par le generateur
      const chartData = {
        client: {
          prenom: clientDetail.prenom,
          nom: clientDetail.nom,
          dateNaissance: clientDetail.dateNaissance,
          heureNaissance: clientDetail.heureNaissance ?? '',
          lieuNaissance: clientDetail.lieuNaissance ?? '',
        },
        planetes: chartDonnees.planetes.map((p) => ({
          ...p,
          maison: typeof p.maison === 'string' ? parseInt(p.maison, 10) : p.maison,
        })),
        maisons: chartDonnees.maisons,
        source: chartDonnees.source ?? 'Olympia',
      };
      await generateNatalDocx(clientDetail, chart, chartData, analysisPreview);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      alert(`Erreur lors de la generation : ${msg}`);
    } finally {
      setGenerating(null);
    }
  }

  /** Generer le document selon le type */
  function handleGenerate(docType: string) {
    if (docType === 'word') {
      handleGenerateWord();
    } else {
      alert(`Generation ${docType} — Bientot disponible`);
    }
  }

  // ─── Filtrage clients ──────────────────────────────────

  const filteredClients = search.trim()
    ? clients.filter((c) => {
        const q = search.toLowerCase();
        return c.prenom.toLowerCase().includes(q) || c.nom.toLowerCase().includes(q);
      })
    : clients;

  const selectedClient = clients.find((c) => c.id === selectedClientId) ?? null;

  // ─── Styles ────────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px 10px 38px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.95rem',
    color: colors.text,
    background: '#fff',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  };

  const canGenerate = !!chartDonnees && !!analysisPreview;

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* En-tete */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <i className="bi bi-file-earmark-zip" style={{ fontSize: 20, color: colors.accent }} />
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600, color: colors.text }}>
            Generateur de documents
          </h1>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: colors.textMid, marginLeft: 30 }}>
          Generez des documents d'interpretation a partir d'un theme natal importe.
        </p>
      </div>

      {/* Etape 1 : Selectionner un client */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div
          style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: selectedClient ? colors.accent : '#f5f5f5',
              color: selectedClient ? '#fff' : colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {selectedClient ? (
              <i className="bi bi-check" style={{ fontSize: 14 }} />
            ) : (
              '1'
            )}
          </span>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: colors.text }}>
            Selectionner un client
          </h2>
        </div>

        <div style={{ padding: '16px 24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: colors.textMuted, fontSize: 14 }}>
              Chargement des clients...
            </div>
          ) : clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <i className="bi bi-people" style={{ fontSize: 32, color: '#ccc', display: 'block', marginBottom: 8 }} />
              <p style={{ fontSize: 14, color: colors.textMuted, marginBottom: 12 }}>
                Aucun client avec un theme natal importe.
              </p>
              <Link
                to="/clients"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  borderRadius: 6,
                  background: colors.accent,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                }}
              >
                <i className="bi bi-plus-lg" style={{ fontSize: 12 }} />
                Importer un theme natal
              </Link>
            </div>
          ) : (
            <>
              {/* Recherche */}
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <i
                  className="bi bi-search"
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 14,
                    color: colors.textMuted,
                  }}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filtrer par nom..."
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = colors.accent;
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = colors.border;
                  }}
                />
              </div>

              {/* Liste */}
              <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filteredClients.map((client) => {
                  const selected = selectedClientId === client.id;
                  return (
                    <button
                      key={client.id}
                      onClick={() => setSelectedClientId(selected ? null : client.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: selected ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                        background: selected ? `${colors.accent}0a` : '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        width: '100%',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: selected ? colors.accent : colors.accentLight,
                          color: selected ? '#fff' : '#8a6d0a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {client.prenom[0]}
                        {client.nom[0]}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                          {client.prenom} {client.nom}
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>
                          {client.charts.some((c) => c.type === 'NATAL') ? 'TN' : ''}{client.charts.some((c) => c.type === 'NATAL') && client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? ' + ' : ''}{client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? 'RS' : ''}
                        </div>
                      </div>
                      {selected && (
                        <i
                          className="bi bi-check-circle-fill"
                          style={{ fontSize: 16, color: colors.accent, flexShrink: 0 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Etape 2 : Apercu des donnees */}
      {selectedClient && (
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <div
            style={{
              padding: '16px 24px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: canGenerate ? colors.accent : '#f5f5f5',
                color: canGenerate ? '#fff' : colors.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {canGenerate ? <i className="bi bi-check" style={{ fontSize: 14 }} /> : '2'}
            </span>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: colors.text }}>
              Apercu des donnees
            </h2>
          </div>

          <div style={{ padding: '16px 24px' }}>
            {loadingPreview ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: colors.textMuted, fontSize: 14 }}>
                Chargement des donnees...
              </div>
            ) : !chartDonnees ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: colors.textMuted, fontSize: 14 }}>
                Aucune donnee de theme natal trouvee pour ce client.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {/* Selecteur de chart si plusieurs */}
                {clientDetail && clientDetail.charts.length > 1 && (
                  <div style={{ gridColumn: '1 / -1', marginBottom: 8 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: colors.textMid, marginBottom: 4, display: 'block' }}>
                      Theme a utiliser :
                    </label>
                    <select
                      value={selectedChartId ?? ''}
                      onChange={(e) => setSelectedChartId(Number(e.target.value))}
                      style={{
                        padding: '8px 12px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: 6,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        background: '#fff',
                        outline: 'none',
                      }}
                    >
                      {clientDetail.charts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.type === 'NATAL' ? 'Theme natal' : c.type === 'REVOLUTION_SOLAIRE' ? `RS ${c.anneeRs}` : c.type}
                          {' — '}importe le {new Date(c.createdAt).toLocaleDateString('fr-FR')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Statistiques */}
                <StatCard
                  icon="bi-globe2"
                  label="Planetes"
                  value={String(chartDonnees.planetes.length)}
                  color="#0369a1"
                  bg="#f0f9ff"
                />
                <StatCard
                  icon="bi-houses"
                  label="Maisons"
                  value={String(chartDonnees.maisons.length)}
                  color="#7c3aed"
                  bg="#f5f3ff"
                />
                <StatCard
                  icon="bi-arrow-left-right"
                  label="Aspects"
                  value={analysisPreview ? String(analysisPreview.aspects.aspects.length) : '...'}
                  color="#16a34a"
                  bg="#f0fdf4"
                />
                <StatCard
                  icon="bi-diagram-3"
                  label="Configurations"
                  value={analysisPreview ? String(analysisPreview.configurations.length) : '...'}
                  color="#ea580c"
                  bg="#fff7ed"
                />

                {/* Resume signature */}
                {analysisPreview && (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      background: colors.accentLight,
                      borderRadius: 8,
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <i className="bi bi-stars" style={{ fontSize: 16, color: colors.accentDark }} />
                    <div style={{ fontSize: 13, color: '#333' }}>
                      <strong>Signature dominante :</strong>{' '}
                      {analysisPreview.signature.dominante.element}{' '}
                      {analysisPreview.signature.dominante.mode}
                    </div>
                  </div>
                )}

                {/* Liste planetes */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Positions
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {chartDonnees.planetes.map((p) => (
                      <span
                        key={p.nom}
                        style={{
                          fontSize: 12,
                          padding: '3px 10px',
                          borderRadius: 20,
                          background: '#f5f5f5',
                          color: colors.textMid,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.nom} {p.signe} {p.retrograde ? 'R' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Etape 3 : Types de documents */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div
          style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#f5f5f5',
              color: colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {selectedClient ? '3' : '2'}
          </span>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: colors.text }}>
            Generer un document
          </h2>
        </div>

        <div style={{ padding: '16px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
            {DOC_TYPES.map((doc) => {
              const enabled = canGenerate && doc.ready;
              const isGenerating = generating === doc.id;
              return (
                <div
                  key={doc.id}
                  style={{
                    padding: '20px',
                    borderRadius: 10,
                    border: `1px solid ${colors.border}`,
                    opacity: canGenerate ? 1 : 0.5,
                    transition: 'opacity 0.2s',
                    position: 'relative',
                  }}
                >
                  {!doc.ready && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: '#f5f5f5',
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Bientot
                    </span>
                  )}
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.3rem',
                      marginBottom: 12,
                      background: doc.bg,
                      color: doc.fg,
                    }}
                  >
                    <i className={`bi ${doc.icon}`} />
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text, marginBottom: 4 }}>
                    {doc.label}
                  </div>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: 11,
                      background: '#f5f5f5',
                      color: colors.textMuted,
                      padding: '2px 8px',
                      borderRadius: 4,
                      marginBottom: 10,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {doc.format}
                  </span>
                  <p style={{ fontSize: 13, color: colors.textMid, lineHeight: 1.6, margin: '0 0 16px' }}>
                    {doc.description}
                  </p>
                  <button
                    disabled={!enabled || isGenerating}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      background: enabled && !isGenerating ? colors.accent : colors.border,
                      color: enabled && !isGenerating ? '#fff' : colors.textMuted,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: enabled && !isGenerating ? 'pointer' : 'not-allowed',
                      fontFamily: 'inherit',
                      transition: 'all 0.15s',
                    }}
                    onClick={() => handleGenerate(doc.id)}
                  >
                    {isGenerating ? (
                      <>
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            border: '2px solid #ccc',
                            borderTopColor: '#fff',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.8s linear infinite',
                          }}
                        />
                        Generation...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-download" style={{ fontSize: 12 }} />
                        Generer
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Note informative */}
      <div
        style={{
          background: colors.accentLight,
          borderRadius: 10,
          padding: '20px 24px',
          borderLeft: `4px solid ${colors.accent}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <i className="bi bi-info-circle" style={{ fontSize: 16, color: colors.accentDark, marginTop: 2, flexShrink: 0 }} />
          <div>
            <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: colors.text }}>
              Comment ca fonctionne
            </p>
            <p style={{ margin: 0, fontSize: 13, color: colors.text, lineHeight: 1.6 }}>
              Importez d'abord un fichier CSV Olympia depuis la fiche d'un client.
              Le generateur utilise les donnees du theme natal (positions planetaires, aspects, configurations)
              pour creer un document d'interpretation complet. Le document est genere entierement
              dans votre navigateur — aucune donnee n'est envoyee a un serveur externe.
            </p>
          </div>
        </div>
      </div>

      {/* Animation spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ─── Sous-composant StatCard ─────────────────────────────

function StatCard({ icon, label, value, color, bg }: {
  icon: string;
  label: string;
  value: string;
  color: string;
  bg: string;
}) {
  return (
    <div
      style={{
        background: bg,
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <i className={`bi ${icon}`} style={{ fontSize: 18, color }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}
